import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import {
	MAT_DIALOG_DATA,
	MatDialogModule,
	MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import {
	ArrangeOptions,
	PromiseToPay,
} from '@lmc-app/core/services/promise-to-pay.service';
import {
	NLS_TRANSACTION_TYPE_LABELS,
	NlsTransactionType,
} from '@lmc-app/shared/enum/promise-to-pay-status.enum';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';

export interface PromiseToPayArrangeDialogData {
	promise: PromiseToPay;
}

/** The chosen options, or undefined when the operator cancelled. */
export type PromiseToPayArrangeDialogResult = ArrangeOptions | undefined;

interface ArrangeFormModel {
	transactionType: NlsTransactionType;
	payoff: boolean;
}

/** Transaction Type / Payoff fields; also used when the edit dialog arranges a promise. */
export function arrangeOptionFields(): FormlyFieldConfig[] {
	return [
		{
			key: 'transactionType',
			type: 'select',
			defaultValue: NlsTransactionType.Payment,
			props: {
				label: 'Transaction Type',
				required: true,
				appearance: 'outline',
				options: Object.values(NlsTransactionType).map((value) => ({
					value,
					label: NLS_TRANSACTION_TYPE_LABELS[value],
				})),
			},
		},
		{
			key: 'payoff',
			type: 'checkbox',
			wrappers: [],
			defaultValue: false,
			props: {
				label: 'Payoff',
				appearance: 'outline',
				floatLabel: 'always',
			},
			// Payoff (NLS Options=4) only means anything on a Payment; a Reduction
			// posts to a different NLS ledger entirely and has no such flag.
			expressions: {
				hide: (field) =>
					field.model?.transactionType !== NlsTransactionType.Payment,
			},
		},
	];
}

/**
 * Collects how an arranged manual_debit promise's amount should be recorded in NLS —
 * a Payment (optionally a Payoff), a Principal Reduction, or an Interest Reduction — before
 * the promise is arranged. It does not call the API itself: the caller's own arrange()
 * request carries the choice and its own busy/error handling, exactly as any other row
 * action does, so this stays a plain, synchronous choice with nothing to load or fail.
 */
@Component({
	selector: 'app-promise-to-pay-arrange-dialog',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		FormlyModule,
		FormlyMaterialModule,
		MatDialogModule,
		MatButtonModule,
	],
	templateUrl: './promise-to-pay-arrange-dialog.component.html',
	styleUrls: ['./promise-to-pay-arrange-dialog.component.scss'],
})
export class PromiseToPayArrangeDialogComponent {
	readonly FormatUtil = FormatUtil;
	readonly form = new FormGroup({});
	model: ArrangeFormModel = {
		transactionType: NlsTransactionType.Payment,
		payoff: false,
	};
	fields: FormlyFieldConfig[] = arrangeOptionFields();

	constructor(
		private readonly dialogRef: MatDialogRef<
			PromiseToPayArrangeDialogComponent,
			PromiseToPayArrangeDialogResult
		>,
		@Inject(MAT_DIALOG_DATA) public readonly data: PromiseToPayArrangeDialogData
	) {}

	onConfirm(): void {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}
		this.dialogRef.close({
			transactionType: this.model.transactionType,
			payoff:
				this.model.transactionType === NlsTransactionType.Payment
					? !!this.model.payoff
					: false,
		});
	}

	onCancel(): void {
		this.dialogRef.close();
	}
}
